import { Component, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { DocumentDistributeObjectService } from '@Core/models/proprietary/document-distribute/document-distribute-object.service';
import { DocumentDistributeObjectState } from '@Core/models/proprietary/document-distribute/document-distribute-object.state';
import { AlertDialogService } from '@Core/services/proprietary/alert-dialog/alert-dialog.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { DocumentDistributeService } from '@Core/services/proprietary/document-distribute.service';
import { DistributeActionType, DistributeResultType } from '@Core/services/proprietary/document-distribute.types';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { SearchService } from '@Core/services/proprietary/search.service';
import { SearchResultType, SearchType } from '@Core/services/proprietary/search.types';

type OptionsType = {
    canMultiSelect: boolean; //not implemented
    canClear: boolean;
    autoSearch: boolean;
    maxRows: number;
    maxRowLimiter: boolean;
};

@Component({
    standalone: false,
    selector: 'document-distribute-dialog',
    templateUrl: './document-distribute-dialog.component.html',
    providers: []
})
export class DocumentDistributeDialogComponent {
    @Select(DocumentDistributeObjectState.documents)
    documents$: Observable<any>;

    @Select(DocumentDistributeObjectState.documentCount)
    documentCount$: Observable<any>;

    @Select(DocumentDistributeObjectState.filings)
    filings$: Observable<any>;

    distMethodByDocID$: Observable<any>;

    filingsByDocID$: Observable<any>;

    _docList: any = [];

    public model = {
        currentDocID: null
    };

    public objectDisplayName: string;
    public objectType: string;
    private documentsSub: Subscription;
    protected parentActiveModal: NgbActiveModal;

    documentDistributePageMenu = {
        objectNode: {
            Menu_Item: [
                {
                    Label: 'Add Recipient',
                    Url: ['/action/addrecipient', {}]
                },
                {
                    Label: 'Clear All Recipients',
                    Url: ['/action/clearrecipients', {}]
                }
            ]
        }
    };

    menuHandler(action: any) {
        switch (action.Action_Code) {
            case 'addrecipient':
                this.recipientSearch();
                break;

            case 'clearrecipients':
                this.documentDistributeOS.removeAllFilingsForDocID(this.model.currentDocID);
                break;

            default:
                console.error('Unhandled Menu Action: ' + JSON.stringify(action));
        }
    }

    constructor(
        private injector: Injector,
        private activeModal: NgbActiveModal,
        private search: SearchService,
        private documentDistributeOS: DocumentDistributeObjectService,
        private store: Store,
        public ds: DataService,
        private route: ActivatedRoute,
        private alertService: AlertDialogService,
        private objectService: ObjectService,
        private distributeService: DocumentDistributeService
    ) {
        documentDistributeOS.setDataService(ds);
    }

    ngOnInit() {
        this.documentsSub = this.documents$.subscribe((docs) => {
            this.setDocList(docs);
            this.filingsByDocID$ = this.store.select(DocumentDistributeObjectState.filingByDocID(this.model.currentDocID));
            this.distMethodByDocID$ = this.store.select(
                DocumentDistributeObjectState.distMethodByDocID(this.model.currentDocID)
            );
        });
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.route.params.subscribe((params) => {
                this.documentDistributeOS.getCorrespondTemplates();
            });
        });
    }

    setDocList(docs: any) {
        if (docs) {
            this._docList = []; //Clear docList first.
            let document: any = {};
            for (var i in docs) {
                if (!this.model.currentDocID) {
                    this.model.currentDocID = docs[i].Doc_ID;
                }
                document = { Code: docs[i].Doc_ID, Code_Desc: docs[i].Doc_Name };
                this._docList.push(document);
                document = {};
            }
        }
    }

    ngOnDestroy() {
        if (this.documentsSub) {
            this.documentsSub.unsubscribe();
        }
    }

    docTrackingID(index: number, object: any) {
        return object.Doc_ID;
    }

    filingTrackingID(index: number, object: any) {
        return object.DocLink_ID;
    }

    initialize(): boolean {
        this.parentActiveModal = this.activeModal;
        return true;
    }

    currentDocChanged() {
        this.filingsByDocID$ = this.store.select(DocumentDistributeObjectState.filingByDocID(this.model.currentDocID));
        this.distMethodByDocID$ = this.store.select(
            DocumentDistributeObjectState.distMethodByDocID(this.model.currentDocID)
        );
    }

    open() {
        this.initialize();
    }

    action(actionType: string) {
        switch (actionType.toLowerCase()) {
            case 'distribute':
                this.distribute('{}');
                break;
            case 'cancel':
                this.cancel();
                break;
            case 'close':
                this.close();
                break;
        }
    }

    closeModal(returnValue: DistributeResultType) {
        if (this.parentActiveModal) {
            this.parentActiveModal.close(returnValue);
            return;
        } else {
            throw 'Search Dialog descendant did not properly set parentActiveModal in "initialize()"';
        }
    }

    close() {
        this.documentDistributeOS.reset();
        this.distributeService.callingObjectCode = null;
        this.closeModal({ action: DistributeActionType.Close });
    }

    cancel() {
        this.documentDistributeOS.reset();
        this.distributeService.callingObjectCode = null;
        this.closeModal({ action: DistributeActionType.Cancel });
    }

    distribute(objectNode: any) {
        const distMethod = this.store.selectSnapshot(
            DocumentDistributeObjectState.distMethodByDocID(this.model.currentDocID)
        );
        if (distMethod && (distMethod == 'Q' || distMethod == 'C')) {
            const hasFilings = this.store.selectSnapshot(
                DocumentDistributeObjectState.filingByDocID(this.model.currentDocID)
            );
            if (hasFilings && hasFilings.length > 0) {
                const allAreFileOnly = hasFilings.every((filing) => filing.File_Only_Ind === 'Y');
                if (allAreFileOnly) {
                    let alertDialog = this.alertService.showAlert(
                        'No Recipient(s) Selected',
                        'You must select one or more recipients to Queue or Correspond.'
                    );
                    alertDialog.then(() => { });
                } else {
                    this.closeModal({ action: DistributeActionType.Distribute });
                }
            }
        } else {
            this.closeModal({ action: DistributeActionType.Distribute });
        }
    }

    recipientSearch() {
        this.search.openDialog(
            SearchType.Entity,
            this.recipientSelected.bind(this),
            { Role_Type: 'BK;CN;SP;EM;CL;BO;TP;TA;FC;PR' },
            { autoSearch: false, allowClear: false, allowMultiSelect: false }
        );
    }

    recipientSelected(result: SearchResultType) {
        if (result.action == 'select') {
            let entityID = result.data[0].Entity_ID;
            let entityName = result.data[0].Entity_Name;
            let roleType = this.getSelectedRole(result);

            let filing = {
                Entity: { Entity_ID: entityID, Entity_Name: entityName, Role_Type: roleType },
                File_Only_Ind: 'N',
                Public_Document_Ind: 'N', //TODO: Default value should come in from Book_Option: Default_Documents_Public_Ind
                File_Drawer: '?',
                File_Folder: '?',
                Delivery_Method: ''
            };

            //When a receipient is added, the object code from the page using the distribute dialog is passed in to retrieve
            //the default folder and drawer
            if (this.distributeService.callingObjectCode) {
                let objectConfig = this.objectService.getObjectConfig(this.distributeService.callingObjectCode);
                filing.File_Folder = objectConfig?.Filing_Defaults?.File_Folder || '?';
                filing.File_Drawer = objectConfig?.Filing_Defaults?.File_Drawer || '?';
            }

            this.documentDistributeOS.addFiling(this.model.currentDocID, filing);
        } else {
            //Do nothing...
        }
    }

    getSelectedRole(result: any) {
        let selectedRole;
        const roles = result.data[0]?.Roles?.Role;

        if (roles && roles.length > 0) {
            for (const role of roles) {
                if ('Selected' in role && role.Selected) {
                    selectedRole = role.Role_Type;
                    break;
                }
            }

            if (!selectedRole) {
                selectedRole = roles[0].Role_Type;
            }
        }

        if (!selectedRole) {
            selectedRole = '';
        }

        return selectedRole;
    }
}
